import { useEffect, useRef, useCallback } from 'react';

interface ScrollPositions {
  [key: string]: number;
}

interface ScrollManagerProps {
  currentPage: string;
  previousPage: string | null;
}

export function ScrollManager({ currentPage, previousPage }: ScrollManagerProps) {
  const scrollPositions = useRef<ScrollPositions>({});
  const isInitialized = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounced scroll position saving
  const saveScrollPosition = useCallback((page: string, position: number) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    scrollTimeoutRef.current = setTimeout(() => {
      scrollPositions.current[page] = position;
    }, 150);
  }, []);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      saveScrollPosition(currentPage, window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [currentPage, saveScrollPosition]);

  // Handle page navigation
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }

    // Save previous page scroll position immediately
    if (previousPage && previousPage !== currentPage) {
      const currentScrollY = window.scrollY;
      scrollPositions.current[previousPage] = currentScrollY;
    }

    // Handle scroll restoration/reset
    const handleScrollRestoration = () => {
      const savedPosition = scrollPositions.current[currentPage];
      
      if (savedPosition !== undefined && savedPosition > 0) {
        // Restore previous scroll position for returning to a page
        requestAnimationFrame(() => {
          window.scrollTo({
            top: savedPosition,
            behavior: 'smooth'
          });
        });
      } else {
        // Scroll to top for new page visits
        requestAnimationFrame(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        });
      }
    };

    // Small delay to ensure content is fully rendered
    const timeoutId = setTimeout(handleScrollRestoration, 100);

    return () => clearTimeout(timeoutId);
  }, [currentPage, previousPage]);

  // Save scroll position before page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      scrollPositions.current[currentPage] = window.scrollY;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [currentPage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  return null; // This component doesn't render anything
}